import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import QuoteModal from "@/components/QuoteModal";
const installImg = "/images/site/walnut-rift-quartered-michigan-plank-floor-dining-area-novi-mi.webp";
const refinishImg = "/images/site/refinished-hardwood-floor-empty-room.webp";
import inspectionImg from "@/assets/nwfa-certified-inspector-certificate.webp";
const subfloorImg = "/images/site/spirit-level-concrete-subfloor-leveling-prep.webp";
import commercialImg from "@/assets/commercial-ann-arbor.webp";
const specConciergeImg = "/images/site/stair-nosing-inlay-detail-dark-stained-landing.webp";
import lvpImg from "@/assets/brand-cali-floors.webp";
import laminateImg from "@/assets/waterproof-laminate-entryway-hero.webp";
import alternaImg from "@/assets/alterna-tile-installation-hero.webp";
import sandFinishImg from "@/assets/pallmann-spider-planetary-sander-dustless-refinishing-michigan.webp";
import restorationImg from "@/assets/northville-walnut-open-floor-kitchen.webp";

const services = [
  {
    title: "Hardwood Floor Installation",
    sub: "We don't just lay floors. We engineer them. From nail down solid hardwood to precision glue down engineered planks, our installers led by an NWFA Certified Installer ensure every board is specified and installed for your home's unique environment.",
    image: installImg,
    imageAlt: "Dining area under drum pendants with a Michigan Plank rift and quartered walnut floor in 2 to 5 inch widths, Novi, MI",
    linkLabel: "View Installation Standards",
    linkHref: "/installation",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    title: "New Site Finished Floors",
    sub: "A new hardwood floor installed, sanded flat and finished in your home. We control every step from product selection to the final coat.",
    image: sandFinishImg,
    imageAlt: "Pallmann Spider planetary sander on a new site finished hardwood floor",
    linkLabel: "Explore Site Finished Floors",
    linkHref: "/sand-and-finish",
    span: "",
  },
  {
    title: "Older Floor Restoration",
    sub: "Waxed, previously sanded or loose older floors get a careful in home assessment first, then a plain English plan for what can be saved.",
    image: restorationImg,
    imageAlt: "Open kitchen with an 8 inch engineered FAS walnut floor, Northville, MI",
    linkLabel: "Explore Floor Restoration",
    linkHref: "/floor-restoration",
    span: "",
  },
  {
    title: "Advanced Dust Control Refinishing",
    sub: "No sanding process is completely clean. Our industrial dual-motor systems achieve 98% containment at the source, paired with instant-cure UV finishes.",
    image: refinishImg,
    imageAlt: "Freshly refinished hardwood floor in an empty room",
    linkLabel: "Explore Refinishing",
    linkHref: "/refinishing",
    span: "md:col-span-2",
  },
  {
    title: "NWFA Certified Inspection",
    sub: "When a floor fails, everyone points fingers. We step in with unbiased, forensic-grade analysis that holds up in court.",
    image: inspectionImg,
    imageAlt: "NWFA Certified Wood Flooring Inspector certificate held by 1 Wood Floors, Livonia, MI",
    linkLabel: "View Inspection Services",
    linkHref: "/inspections",
    span: "md:row-span-2",
  },
  {
    title: "Luxury Vinyl Plank Installation",
    sub: "SPC vs WPC core science, forensic subfloor prep, and the 1/8-Inch Mandate that prevents snapped tongues and grooves.",
    image: lvpImg,
    imageAlt: "Cali luxury vinyl plank floor",
    linkLabel: "View LVP Installation",
    linkHref: "/lvp-installation",
    span: "",
  },
  {
    title: "Subfloor Leveling & Repair",
    sub: "A stunning floor starts beneath the surface. We correct the dips, crowns, and structural issues most contractors ignore.",
    image: subfloorImg,
    imageAlt: "Spirit level checking a concrete subfloor during leveling prep",
    linkLabel: "Learn About Subfloor Prep",
    linkHref: "/subfloor-leveling",
    span: "",
  },
  {
    title: "Technical Specification Concierge",
    sub: "Leveraging our manufacturing and NWFA inspection expertise to specify the exact right material for your unique environment. We don't just sell wood  we mitigate risk by analyzing subfloor compatibility, moisture mechanics, and wear layer requirements for high performance Michigan homes.",
    image: specConciergeImg,
    imageAlt: "Custom stair nosing and inlay detail on a dark stained hardwood landing",
    linkLabel: "Explore Product Specification",
    linkHref: "/product-specification",
    span: "md:col-span-2",
  },
  {
    title: "Waterproof Laminate Installation",
    sub: "Quick-Step Uniclic technology with flush-mount stair nosings and TOP Step matching vents for architectural continuity since 1998.",
    image: laminateImg,
    imageAlt: "Waterproof laminate entryway floor with flush stair nosings",
    linkLabel: "View Laminate Installation",
    linkHref: "/laminate-installation",
    span: "md:col-span-2",
  },
  {
    title: "Alterna Engineered Tile Installation",
    sub: "Flush-transition engineering with Schluter metal trims creating seamless flows between tile and hardwood. No trip hazards.",
    image: alternaImg,
    imageAlt: "Armstrong Alterna engineered tile with a flush transition to a hardwood floor",
    linkLabel: "View Alterna Installation",
    linkHref: "/alterna-tile-installation",
    span: "",
  },
  {
    title: "Commercial Projects",
    sub: "From tech campus lobbies to luxury retail flagships. NWFA-certified precision at scale.",
    image: commercialImg,
    imageAlt: "Commercial staircase refinished with Pallmann Magic Oil at a tech headquarters, Ann Arbor, MI",
    linkLabel: "View Commercial Services",
    linkHref: "/commercial",
    span: "",
  },
];

const ServicesSection = () => (
  <section className="bg-background py-14 md:py-20 lg:py-28" id="services">
    <div className="max-w-[1200px] mx-auto px-5 md:px-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-10"
      >
        <div>
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-3">
            Our Services
          </p>
          <h2 className="font-display text-[clamp(28px,5vw,52px)] font-semibold text-foreground leading-[1.1]">
            Every phase, <em className="italic font-normal text-primary">done properly.</em>
          </h2>
          <p className="mt-3 md:mt-4 text-[13px] md:text-[15px] font-light text-muted-foreground leading-relaxed max-w-xl">
            We treat every project like it's our own home. Our team brings NWFA certified expertise to every phase, from specification to final finish.
          </p>
          <p className="mt-3 md:mt-4 text-[13px] md:text-[15px] font-light text-muted-foreground leading-relaxed max-w-xl">
            Every floor we install meets or exceeds National Wood Flooring Association (NWFA) industry standard guidelines.
          </p>
        </div>
        <QuoteModal
          trigger={
            <button className="mt-4 md:mt-0 inline-flex items-center gap-2 border border-border text-foreground px-6 py-3 rounded-full text-xs font-medium hover:border-primary/30 hover:shadow-md transition-all self-start md:self-auto">
              Request a Consultation →
            </button>
          }
        />
      </motion.div>

      {/* Seamless Mosaic */}
      <div className="rounded-[28px] overflow-hidden">
        <div
          className="grid grid-cols-1 md:grid-cols-4 gap-[4px]"
          style={{ gridAutoFlow: "dense", gridAutoRows: "minmax(220px, 1fr)" }}
        >
          {services.map((svc, i) => {
            const isLarge = svc.span.includes("col-span-2") && svc.span.includes("row-span-2");

            return (
              <motion.div
                key={svc.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className={`relative overflow-hidden group min-h-[280px] md:min-h-0 ${svc.span}`}
              >
                <Link to={svc.linkHref} className="absolute inset-0 block">
                  <img width={800} height={534} loading="lazy"
                    src={svc.image}
                    alt={svc.imageAlt}
                    className={`absolute inset-0 w-full h-full object-cover grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.05] transition-all duration-[550ms] ease-[cubic-bezier(.25,.46,.45,.94)] ${svc.title === "NWFA Certified Inspection" ? "object-[center_30%]" : ""}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className={`absolute bottom-0 left-0 ${isLarge ? "p-5 md:p-7" : "p-4 md:p-5"}`}>
                    <h3 className={`font-display font-semibold text-primary-foreground leading-tight ${isLarge ? "text-lg md:text-[26px]" : "text-base md:text-base"}`}>
                      {svc.title}
                    </h3>
                    <p className={`font-light text-primary-foreground/70 mt-1 leading-relaxed ${isLarge ? "text-sm md:text-sm max-w-sm" : "text-[14px] md:text-[11px] max-w-xs"}`}>
                      {svc.sub}
                    </p>
                    <span className="inline-flex items-center gap-1 text-[14px] md:text-[11px] font-semibold tracking-wide text-gold-400 mt-2 group-hover:text-gold-300 transition-colors">
                      {svc.linkLabel} <ArrowRight className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

export default ServicesSection;
